import React from 'react'
import styled from '@emotion/styled'
import { Icon, type IconName } from '@mobi/component-library/Common/V2/Icon/Icon'
import { colors, font, measurements, radius, spacing } from '@mobi/component-library/Theme/Common'
import { shouldApplySafeAreaBottomPadding } from '@core/Settings/cssConditions'

// TODO: Move to component library when removing shouldApplySafeAreaBottomPadding support

export const FloatingActionButton: React.FC<{
  iconName: IconName
  onClick(): void
  label?: string
  tooltipRef?: React.RefObject<HTMLDivElement>
}> = ({ iconName, onClick, label, tooltipRef }) => {
  const [isScrolling, setIsScrolling] = React.useState(false)

  React.useEffect(() => {
    if (!label) return
    const controller = new AbortController()
    const signal = controller.signal
    window.addEventListener('scroll', () => setIsScrolling(window.scrollY > 100), { signal })
    return () => controller.abort()
  }, [label])

  const classnames = []
  if (label && isScrolling) classnames.push('fab-button--hide-label')
  if (label && !isScrolling) classnames.push('fab-button--expand')

  return (
    <>
      <FabContainerStyled ref={tooltipRef} className={classnames.join(' ')}>
        <button type='button' onClick={onClick}>
          {label && (
            <span className='fab-button__label'>
              <span>
                <span>{label}</span>
              </span>
            </span>
          )}

          <span className='fab-button__icon'>
            <Icon name={iconName} size='2.4rem' />
          </span>
        </button>
      </FabContainerStyled>

      {/* Empty div to pad bottom of feed, no content clip */}
      <div />
    </>
  )
}

// ======
// Styles
// ======

const FabContainerStyled = styled.div({
  '--buttonSize': '5.6rem',
  '--buttonSizeMinusBorder': 'calc(var(--buttonSize) - 0.2rem)',
  '--iconSize': '2.4rem',

  boxSizing: 'border-box',
  position: 'fixed',
  zIndex: 1,
  right: `calc(max(${spacing.smx1}, 50vw - (${measurements.mobi.maxWidth} / 2) + ${spacing.smx1}))`,
  bottom: shouldApplySafeAreaBottomPadding
    ? `calc(${spacing.smx1} + ${measurements.mobi.bottomTabsHeight} + var(--safe-area-inset-bottom, 0px))`
    : spacing.smx1,

  '> button': {
    boxSizing: 'border-box',
    position: 'relative',
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'stretch',
    justifyContent: 'center',
    height: 'var(--buttonSize)',
    minWidth: 'var(--buttonSize)',
    padding: 0,

    fontFamily: font.family.primary,
    fontWeight: font.weight.semibold,
    fontSize: font.size.xl.fontSize,
    letterSpacing: font.size.xl.letterSpacing,
    lineHeight: font.size.xl.lineHeight,

    backgroundColor: colors.studio[500],
    border: `0.1rem solid ${colors.studio[500]}`,
    color: colors.white,
    borderRadius: radius.pill,
    cursor: 'pointer',
    touchAction: 'manipulation',
    transition: 'all 0.25s ease',
    userSelect: 'none',
    WebkitTapHighlightColor: colors.transparent,
    filter:
      'drop-shadow(0px 22px 6px rgba(0, 0, 0, 0.00)) drop-shadow(0px 14px 6px rgba(0, 0, 0, 0.02)) drop-shadow(0px 8px 5px rgba(0, 0, 0, 0.08)) drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.13)) drop-shadow(0px 1px 2px rgba(0, 0, 0, 0.15))',

    '@media (hover: hover) and (pointer: fine)': {
      '&:hover': {
        background: colors.studio[600],
        border: `0.1rem solid ${colors.studio[600]}`,

        '.fab-button__icon': {
          svg: {
            backgroundColor: colors.studio[600],
          },
        },
      },
    },

    '&:active': {
      transform: 'scale(0.95)',
      background: colors.studio[700],
      border: `0.1rem solid ${colors.studio[700]}`,

      '.fab-button__label > span': {
        opacity: 0.75,
      },

      '.fab-button__icon svg': {
        opacity: 0.75,
        backgroundColor: colors.studio[700],
      },
    },

    '.fab-button__label': {
      position: 'relative',
      zIndex: 1,
      display: 'grid',
      gridTemplateColumns: '1fr',
      height: 'var(--buttonSizeMinusBorder)',
      transitionProperty: 'grid-template-columns',
      transitionDuration: '0.6s',

      '> span': {
        overflow: 'hidden',
        gridRow: '1 / span2',
        display: 'flex',
        alignItems: 'center',
        textOverflow: 'clip',
        whiteSpace: 'nowrap',
        transition: 'opacity 0.25s ease',

        '> span': {
          transition: 'opacity 0.3s ease',
          paddingLeft: spacing.bigx1,
          // paddingRight = padding + gap + icon width
          paddingRight: `calc(${spacing.bigx1} + ${spacing.smx1} + var(--iconSize))`,
        },
      },
    },

    '.fab-button__icon': {
      position: 'absolute',
      zIndex: 2,
      top: 0,
      right: 0,
      height: 'var(--buttonSizeMinusBorder)',
      width: 'var(--buttonSizeMinusBorder)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transform: 'translateX(0)',
      transition: 'transform 0.6s ease',

      svg: {
        transition: 'all 0.25s ease',
        backgroundColor: colors.studio[500],
      },
    },
  },

  '&.fab-button--expand': {
    '.fab-button__label > span > span': { opacity: 1 },
    '.fab-button__icon': { transform: 'translateX(-0.9rem)' },
  },

  '&.fab-button--hide-label': {
    '.fab-button__label': {
      gridTemplateColumns: '0fr',

      '> span > span': { opacity: 0 },
    },
  },

  // Empty div to pad bottom of feed, no content clip
  '& + div': { minHeight: '5.6rem' },
})
